/**
 * Extracts the top N dominant colors from an image
 * @param imageUrl - URL of the image to analyze
 * @param colorCount - Number of colors to extract (default: 4)
 * @returns Promise resolving to array of hex color codes
 */
export const extractDominantColors = async (
  imageUrl: string,
  colorCount: number = 4
): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Set canvas size (resize for performance)
        const maxSize = 200;
        const scale = Math.min(maxSize / img.width, maxSize / img.height);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Count color frequencies
        const colorMap = new Map<string, number>();
        
        // Sample pixels (every 4th pixel for performance)
        for (let i = 0; i < data.length; i += 16) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          
          // Skip transparent pixels
          if (a < 128) continue;
          
          // Quantize colors to reduce noise (group similar colors)
          const quantizedR = Math.floor(r / 16) * 16;
          const quantizedG = Math.floor(g / 16) * 16;
          const quantizedB = Math.floor(b / 16) * 16;
          
          const colorKey = `${quantizedR},${quantizedG},${quantizedB}`;
          colorMap.set(colorKey, (colorMap.get(colorKey) || 0) + 1);
        }
        
        // Convert to array and sort by frequency
        const colors = Array.from(colorMap.entries())
          .map(([rgb, count]) => {
            const [r, g, b] = rgb.split(',').map(Number);
            return {
              rgb: { r, g, b },
              hex: `#${[r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')}`,
              count
            };
          })
          .sort((a, b) => b.count - a.count)
          .slice(0, colorCount)
          .map(item => item.hex);
        
        resolve(colors);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    // Handle CORS issues by trying to load the image
    img.src = imageUrl;
  });
};

/**
 * Applies color theme to an element using CSS variables
 * @param element - Element to apply theme to
 * @param colors - Array of hex color codes
 */
export const applyColorTheme = (element: HTMLElement | null, colors: string[]): void => {
  if (!element || !colors || colors.length === 0) return;
  
  colors.forEach((color, index) => {
    element.style.setProperty(`--theme-color-${index + 1}`, color);
  });
};

